if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async addArtskoolMembership(mainResponse) {
        const section = this.closest('[id^="MainProduct-"]');
        const membership = section?.querySelector('[data-artskool-membership]');
        const toggle = membership?.querySelector('[data-artskool-membership-toggle]');
        const errorEl = membership?.querySelector('[data-membership-error]');
        if (!membership || !toggle?.checked) return mainResponse;

        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }

        const variantId = membership.dataset.membershipVariantId;
        const sellingPlanId = membership.dataset.membershipSellingPlanId;
        if (!variantId || !sellingPlanId) throw new Error('Membership product or subscription selling plan is missing.');

        const root = window.Shopify?.routes?.root || '/';
        const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        const cart = await cartResponse.json();
        const membershipLines = Array.isArray(cart.items)
          ? cart.items.filter((item) => String(item.variant_id) === String(variantId))
          : [];
        if (membershipLines.length) {
          const keep = membershipLines.find((item) =>
            String(item.selling_plan_allocation?.selling_plan?.id || '') === String(sellingPlanId)
          ) || membershipLines[0];
          const updates = {};
          membershipLines.forEach((item) => {
            updates[item.key] = item.key === keep.key ? 1 : 0;
          });
          const normalizePayload = { updates };
          if (this.cart) {
            normalizePayload.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
            normalizePayload.sections_url = window.location.pathname;
          }
          const normalizeResponse = await fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(normalizePayload),
          });
          const normalizedCart = await normalizeResponse.json();
          if (!normalizeResponse.ok || normalizedCart.status) {
            throw new Error(normalizedCart.description || normalizedCart.message || 'Membership quantity could not be corrected.');
          }
          return normalizedCart.sections ? normalizedCart : mainResponse;
        }

        const payload = { items: [{ id: Number(variantId), quantity: 1, selling_plan: Number(sellingPlanId) }] };
        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
          payload.sections_url = window.location.pathname;
        }

        const response = await fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload),
        });
        const membershipResponse = await response.json();
        if (!response.ok || membershipResponse.status) {
          throw new Error(membershipResponse.description || membershipResponse.message || 'Membership could not be added.');
        }
        return membershipResponse.sections ? membershipResponse : mainResponse;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              await this.addArtskoolMembership(response);
              window.location = window.routes.cart_url;
              return;
            }

            try {
              response = await this.addArtskoolMembership(response);
            } catch (membershipError) {
              const membership = this.closest('[id^="MainProduct-"]')?.querySelector('[data-artskool-membership]');
              const errorEl = membership?.querySelector('[data-membership-error]');
              if (errorEl) {
                errorEl.textContent = membershipError.message;
                errorEl.hidden = false;
              }
              throw membershipError;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cartData: response });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
